import { ColumnMeta, FilterValue } from './types';

/** Read a dot-path ('customer.name') from a row; undefined when missing. */
export function getAtPath(row: any, path: string): any {
  if (row === null || row === undefined) return undefined;
  let cur: any = row;
  for (const p of path.split('.')) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[p];
  }
  return cur;
}

export function isEmptyValue(v: any): boolean {
  return v === null || v === undefined || v === '' ||
    (typeof v === 'number' && Number.isNaN(v));
}

/** Parse a date-ish value to a timestamp (ms); null when unparseable/empty. */
export function parseDateValue(v: any): number | null {
  if (isEmptyValue(v)) return null;
  if (typeof v === 'number') return v;
  const t = Date.parse(String(v));
  return Number.isNaN(t) ? null : t;
}

/** True when the string contains Han ideographs
 *  (CJK Extension A, Unified Ideographs, Compatibility Ideographs). */
function containsHan(s: string): boolean {
  for (const ch of s) {
    const c = ch.codePointAt(0) as number;
    if ((c >= 0x3400 && c <= 0x4dbf) || (c >= 0x4e00 && c <= 0x9fff) ||
        (c >= 0xf900 && c <= 0xfaff)) return true;
  }
  return false;
}

/**
 * Locale-aware text compare: case-insensitive, natural numbers, latin before CJK.
 * zh-Hant collation orders Han after Latin, so script tier is compared first;
 * within a tier zh-Hant collation gives case-insensitive natural ordering.
 */
export function compareText(a: string, b: string): number {
  const cjkA = containsHan(String(a));
  const cjkB = containsHan(String(b));
  if (cjkA !== cjkB) return cjkA ? 1 : -1;
  return String(a).localeCompare(String(b), 'zh-Hant', { numeric: true, sensitivity: 'base' });
}

/**
 * Direction-aware row comparator for one column. Empty values always sort
 * LAST regardless of direction; equal keys keep original order (stable sort).
 */
export function makeComparator(col: ColumnMeta, dir: 'asc' | 'desc'): (a: any, b: any) => number {
  const { key, type } = col;
  const desc = dir === 'desc';
  return (rowA: any, rowB: any) => {
    const a = getAtPath(rowA, key);
    const b = getAtPath(rowB, key);
    const aEmpty = isEmptyValue(a);
    const bEmpty = isEmptyValue(b);
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1; // empties last, even when desc
    if (bEmpty) return -1;
    let r = 0;
    if (type === 'number') {
      r = Number(a) - Number(b);
    } else if (type === 'date') {
      // Unparseable dates sort last, mirroring the empties-last rule above.
      const ta = parseDateValue(a);
      const tb = parseDateValue(b);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      r = ta - tb;
    } else if (type === 'select') {
      const vals = col.selectValues || [];
      const ia = vals.indexOf(String(a));
      const ib = vals.indexOf(String(b));
      r = (ia === -1 ? vals.length : ia) - (ib === -1 ? vals.length : ib);
      if (r === 0) r = compareText(String(a), String(b));
    } else {
      r = compareText(String(a), String(b));
    }
    return desc ? -r : r;
  };
}

/** True when a filter carries no constraint (treated as "no filter"). */
export function isNoOpFilter(f: FilterValue | undefined): boolean {
  if (!f) return true;
  if (f.type === 'text') return !f.q.trim();
  if (f.type === 'select') return f.value === '';
  if (f.type === 'number') return f.v1 === undefined && f.v2 === undefined;
  if (f.type === 'date') return !f.from && !f.to;
  return false;
}

/** True when a raw cell value passes an active filter. */
export function matchesFilter(raw: any, f: FilterValue): boolean {
  switch (f.type) {
    case 'text':
      return String(raw ?? '').toLowerCase().includes(f.q.trim().toLowerCase());
    case 'number': {
      if (f.v1 === undefined && f.v2 === undefined) return true;
      const n = Number(raw);
      if (Number.isNaN(n)) return f.op === 'ne';
      if (f.op === 'between') {
        if (f.v1 !== undefined && n < f.v1) return false;
        if (f.v2 !== undefined && n > f.v2) return false;
        return true;
      }
      const v = f.v1 as number;
      switch (f.op) {
        case 'eq': return n === v;
        case 'ne': return n !== v;
        case 'gt': return n > v;
        case 'lt': return n < v;
        case 'ge': return n >= v;
        case 'le': return n <= v;
      }
      return true;
    }
    case 'date': {
      if (!f.from && !f.to) return true;
      const t = parseDateValue(raw);
      if (t === null) return false;
      if (f.from && t < Date.parse(f.from)) return false;
      if (f.to && t > Date.parse(f.to) + 86399999) return false; // inclusive end-of-day
      return true;
    }
    case 'select':
      return String(raw ?? '') === f.value;
  }
}
